/**
 * @module features/client-device-management/components/WifiVaultPanel
 * @description Organization WiFi Network Vault management panel inside `{Org} Device Management`.
 * Supports creating, editing, audited AES-256 passphrase reveal/copy, and removing reusable
 * organization WiFi profiles so Super Admins can manage all WiFi details in one place.
 */
import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Pencil,
  Trash2,
  Wifi,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { AppError } from "@/lib/api-client";
import {
  CreateWifiVaultFormSchema,
  CreateWifiVaultFormValues,
  UpdateWifiVaultFormSchema,
  UpdateWifiVaultFormValues,
  WifiVaultItem,
} from "../types";
import {
  useCreateOrgWifiNetwork,
  useDeleteOrgWifiNetwork,
  useOrgWifiVault,
  useRevealOrgWifiNetworkPassword,
  useUpdateOrgWifiNetwork,
} from "../api/wifiApi";

export interface WifiVaultPanelProps {
  clientId: string;
  orgName: string;
}

export function WifiVaultPanel({ clientId, orgName }: WifiVaultPanelProps) {
  const vaultQuery = useOrgWifiVault(clientId);
  const createMutation = useCreateOrgWifiNetwork(clientId);
  const updateMutation = useUpdateOrgWifiNetwork(clientId);
  const deleteMutation = useDeleteOrgWifiNetwork(clientId);
  const revealMutation = useRevealOrgWifiNetworkPassword(clientId);

  const [editingNetwork, setEditingNetwork] =
    React.useState<WifiVaultItem | null>(null);
  const [showPasswordInput, setShowPasswordInput] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<AppError | null>(null);
  const [savedNotice, setSavedNotice] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [confirmDeleteItem, setConfirmDeleteItem] =
    React.useState<WifiVaultItem | null>(null);

  // Ephemeral decrypted passphrase map (never cached in TanStack Query; wiped on unmount)
  const [revealedPasswords, setRevealedPasswords] = React.useState<
    Record<string, string>
  >({});
  const [revealingId, setRevealingId] = React.useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    setRevealedPasswords({});
    setEditingNetwork(null);
    setConfirmDeleteItem(null);
  }, [clientId]);

  React.useEffect(() => {
    return () => {
      setRevealedPasswords({});
    };
  }, []);

  const activeSchema = editingNetwork
    ? UpdateWifiVaultFormSchema
    : CreateWifiVaultFormSchema;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateWifiVaultFormValues | UpdateWifiVaultFormValues>({
    resolver: zodResolver(activeSchema) as any,
    defaultValues: {
      network_name: "",
      ssid_name: "",
      password: "",
      notes: "",
    },
  });

  const handleStartEdit = (item: WifiVaultItem): void => {
    setEditingNetwork(item);
    setConfirmDeleteItem(null);
    setShowPasswordInput(false);
    setSubmitError(null);
    setSavedNotice(null);
    reset({
      network_name: item.network_name,
      ssid_name: item.ssid_name,
      password: "",
      notes: item.notes ?? "",
    });
  };

  const handleCancelEdit = (): void => {
    setEditingNetwork(null);
    setShowPasswordInput(false);
    setSubmitError(null);
    reset({
      network_name: "",
      ssid_name: "",
      password: "",
      notes: "",
    });
  };

  const onFormSubmit = async (
    values: CreateWifiVaultFormValues | UpdateWifiVaultFormValues
  ): Promise<void> => {
    setSubmitError(null);
    setSavedNotice(null);
    try {
      if (editingNetwork) {
        const updated = await updateMutation.mutateAsync({
          networkId: editingNetwork.id,
          input: values as UpdateWifiVaultFormValues,
        });
        // Purge any revealed secret in case passphrase rotated
        setRevealedPasswords((prev) => {
          const next = { ...prev };
          delete next[editingNetwork.id];
          return next;
        });
        setEditingNetwork(null);
        setShowPasswordInput(false);
        reset({
          network_name: "",
          ssid_name: "",
          password: "",
          notes: "",
        });
        setSavedNotice(
          `Updated "${updated.network_name}" (${updated.ssid_name}) in vault.`
        );
      } else {
        const created = await createMutation.mutateAsync(
          values as CreateWifiVaultFormValues
        );
        reset({
          network_name: "",
          ssid_name: "",
          password: "",
          notes: "",
        });
        setShowPasswordInput(false);
        setSavedNotice(
          `Saved "${created.network_name}" (${created.ssid_name}) to vault.`
        );
      }
    } catch (rawErr: unknown) {
      setSubmitError(
        AppError.fromUnknown(
          rawErr,
          editingNetwork
            ? "Failed to update WiFi vault profile."
            : "Failed to create WiFi vault profile."
        )
      );
    }
  };

  const handleToggleRevealVaultPassword = async (
    item: WifiVaultItem
  ): Promise<void> => {
    if (revealedPasswords[item.id] !== undefined) {
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      return;
    }

    setRevealingId(item.id);
    setSubmitError(null);
    try {
      const plaintext = await revealMutation.mutateAsync(item.id);
      setRevealedPasswords((prev) => ({ ...prev, [item.id]: plaintext }));
    } catch (rawErr: unknown) {
      setSubmitError(
        AppError.fromUnknown(rawErr, "Failed to reveal vault WiFi passphrase.")
      );
    } finally {
      setRevealingId(null);
    }
  };

  const handleCopyVaultPassword = async (
    networkId: string
  ): Promise<void> => {
    const secret = revealedPasswords[networkId];
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedId(networkId);
      setTimeout(() => {
        setCopiedId((curr) => (curr === networkId ? null : curr));
      }, 2000);
    } catch {
      setSubmitError(
        new AppError({
          message:
            "Clipboard permission denied. Copy the revealed passphrase manually.",
        })
      );
    }
  };

  const handleConfirmDelete = async (item: WifiVaultItem): Promise<void> => {
    setDeletingId(item.id);
    setSubmitError(null);
    setSavedNotice(null);
    try {
      await deleteMutation.mutateAsync(item.id);
      setConfirmDeleteItem(null);
      if (editingNetwork?.id === item.id) {
        handleCancelEdit();
      }
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      setSavedNotice(`Removed "${item.network_name}" from vault.`);
    } catch (rawErr: unknown) {
      setSubmitError(
        AppError.fromUnknown(rawErr, "Failed to remove WiFi vault profile.")
      );
    } finally {
      setDeletingId(null);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
      {/* Reusable Vault Profiles List */}
      <div className="lg:col-span-7 space-y-3">
        <div className="rounded-2xl border border-border/80 bg-card p-5 shadow-card space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-tile text-tile-foreground shrink-0">
              <Wifi className="h-4 w-4" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-[15px] leading-5 font-medium text-foreground">
                Organization WiFi Network Vault
              </h2>
              <p className="text-xs text-muted-foreground">
                Reusable WPA2/WPA3 profiles for {orgName}. Passphrases are
                encrypted at rest with AES-256.
              </p>
            </div>
          </div>

          {vaultQuery.isLoading ? (
            <div className="space-y-3" aria-busy="true">
              <Skeleton className="h-24 w-full rounded-xl" />
              <Skeleton className="h-24 w-full rounded-xl" />
            </div>
          ) : vaultQuery.isError ? (
            <div
              role="alert"
              className="rounded-xl border border-danger/20 bg-danger-soft p-4 text-xs text-danger space-y-2"
            >
              <p>
                {AppError.fromUnknown(vaultQuery.error).errorDetails ||
                  "Unable to load WiFi vault profiles."}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void vaultQuery.refetch()}
                className="bg-card text-foreground text-xs"
              >
                Retry
              </Button>
            </div>
          ) : (vaultQuery.data ?? []).length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-center">
              <p className="text-sm font-medium text-foreground">
                No WiFi profiles in {orgName}&apos;s vault yet
              </p>
              <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
                Add office, warehouse, or floor WiFi networks once and attach
                them to assigned devices without re-typing credentials.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {(vaultQuery.data ?? []).map((item) => {
                const isConfirming = confirmDeleteItem?.id === item.id;
                const isEditingThis = editingNetwork?.id === item.id;
                const revealedSecret = revealedPasswords[item.id];
                const isRevealed = revealedSecret !== undefined;

                return (
                  <div
                    key={item.id}
                    className={`rounded-xl border p-3.5 space-y-2.5 transition-colors ${
                      isEditingThis
                        ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                        : "border-border/70 bg-muted/20"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 space-y-0.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-foreground truncate">
                            {item.network_name}
                          </p>
                          <span className="inline-flex items-center rounded-full bg-success-soft text-success border border-success/20 px-2 py-0.5 text-[10px] font-medium">
                            Active
                          </span>
                          {isEditingThis && (
                            <span className="inline-flex items-center rounded-full bg-muted text-foreground border border-border px-2 py-0.5 text-[10px] font-medium">
                              Editing
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          SSID:{" "}
                          <span className="font-mono text-foreground">
                            {item.ssid_name}
                          </span>
                        </p>
                        {item.notes && (
                          <p className="text-[11px] text-muted-foreground pt-0.5">
                            {item.notes}
                          </p>
                        )}
                      </div>

                      {!isConfirming ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleStartEdit(item)}
                            aria-label={`Edit ${item.network_name}`}
                            className="h-8 touch:min-h-[44px] px-2.5 text-xs gap-1 bg-card"
                          >
                            <Pencil
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                            <span>Edit</span>
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfirmDeleteItem(item)}
                            aria-label={`Remove ${item.network_name} from vault`}
                            className="h-8 w-8 touch:min-h-[44px] touch:min-w-[44px] text-muted-foreground hover:text-danger hover:bg-danger-soft shrink-0"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={deletingId === item.id}
                            onClick={() => setConfirmDeleteItem(null)}
                            className="h-8 touch:min-h-[44px] px-2.5 text-xs"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            disabled={deletingId === item.id}
                            onClick={() => void handleConfirmDelete(item)}
                            className="h-8 touch:min-h-[44px] px-2.5 text-xs"
                          >
                            {deletingId === item.id ? (
                              <Loader2
                                className="h-3.5 w-3.5 animate-spin"
                                aria-hidden="true"
                              />
                            ) : (
                              "Remove"
                            )}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Masked / Audited Reveal WPA Passphrase Bar */}
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-border/70 bg-card px-3 py-1.5">
                      <div className="min-w-0 text-xs">
                        <span className="text-[11px] text-muted-foreground mr-2">
                          Passphrase:
                        </span>
                        <span className="font-mono text-foreground break-all">
                          {isRevealed
                            ? revealedSecret
                            : item.password || "••••••••"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={revealingId === item.id}
                          onClick={() =>
                            void handleToggleRevealVaultPassword(item)
                          }
                          aria-label={
                            isRevealed
                              ? `Hide passphrase for ${item.network_name}`
                              : `Reveal passphrase for ${item.network_name}`
                          }
                          className="h-7 w-7 touch:min-h-[44px] touch:min-w-[44px]"
                        >
                          {revealingId === item.id ? (
                            <Loader2
                              className="h-3.5 w-3.5 animate-spin"
                              aria-hidden="true"
                            />
                          ) : isRevealed ? (
                            <EyeOff
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                          ) : (
                            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                          )}
                        </Button>
                        {isRevealed && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              void handleCopyVaultPassword(item.id)
                            }
                            aria-label={`Copy passphrase for ${item.network_name}`}
                            className="h-7 w-7 touch:min-h-[44px] touch:min-w-[44px]"
                          >
                            {copiedId === item.id ? (
                              <Check
                                className="h-3.5 w-3.5 text-success"
                                aria-hidden="true"
                              />
                            ) : (
                              <Copy
                                className="h-3.5 w-3.5"
                                aria-hidden="true"
                              />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                    {isConfirming && (
                      <p className="text-[11px] text-warning bg-warning-soft border border-warning/20 rounded-lg px-2.5 py-1.5">
                        Removing this vault network detaches it from any devices
                        currently referencing it.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Vault Profile Form */}
      <div className="lg:col-span-5">
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          noValidate
          className="rounded-2xl border border-border/80 bg-card p-5 shadow-card space-y-4"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-[15px] leading-5 font-medium text-foreground">
                {editingNetwork
                  ? `Edit Vault WiFi Profile`
                  : "Add Vault WiFi Profile"}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {editingNetwork
                  ? `Modifying "${editingNetwork.network_name}". Leave passphrase blank to keep existing credentials.`
                  : "Stored with AES-256 symmetric encryption at rest."}
              </p>
            </div>
            {editingNetwork ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancelEdit}
                className="h-8 touch:min-h-[44px] px-2 text-xs gap-1 shrink-0"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Cancel</span>
              </Button>
            ) : (
              <Lock
                className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5"
                aria-hidden="true"
              />
            )}
          </div>

          {submitError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger"
            >
              <AlertCircle
                className="h-4 w-4 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p>{submitError.errorDetails}</p>
                {submitError.requestId && (
                  <p className="mt-1 font-mono text-[11px] text-muted-foreground break-all">
                    Request ID: {submitError.requestId}
                  </p>
                )}
              </div>
            </div>
          )}

          {savedNotice && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-xl border border-success/20 bg-success-soft p-3 text-xs text-success"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{savedNotice}</span>
            </div>
          )}

          <fieldset className="space-y-4">
            <legend className="sr-only">
              {editingNetwork
                ? "Edit organization WiFi vault profile"
                : "Create organization WiFi vault profile"}
            </legend>

            <div className="space-y-1.5">
              <label
                htmlFor="vault-network-name"
                className="text-xs font-medium text-foreground block"
              >
                Profile Label <span className="text-danger">*</span>
              </label>
              <Input
                id="vault-network-name"
                autoComplete="off"
                placeholder="e.g. HQ 4th Floor WLAN"
                {...register("network_name")}
                className="h-11 sm:h-10 text-base sm:text-sm bg-card"
              />
              {errors.network_name && (
                <p role="alert" className="text-[11px] text-danger">
                  {errors.network_name.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="vault-ssid-name"
                className="text-xs font-medium text-foreground block"
              >
                WiFi SSID (1–32 chars) <span className="text-danger">*</span>
              </label>
              <Input
                id="vault-ssid-name"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="e.g. Corp-IoT-5G"
                {...register("ssid_name")}
                className="h-11 sm:h-10 text-base sm:text-sm font-mono bg-card"
              />
              {errors.ssid_name && (
                <p role="alert" className="text-[11px] text-danger">
                  {errors.ssid_name.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="vault-wifi-password"
                className="text-xs font-medium text-foreground block"
              >
                WPA2/WPA3 Passphrase (8–64 chars){" "}
                {editingNetwork ? (
                  <span className="text-[11px] font-normal text-muted-foreground">
                    (Optional)
                  </span>
                ) : (
                  <span className="text-danger">*</span>
                )}
              </label>
              <div className="relative">
                <Input
                  id="vault-wifi-password"
                  type={showPasswordInput ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder={
                    editingNetwork
                      ? "Leave blank to keep current encrypted passphrase"
                      : "Minimum 8 characters"
                  }
                  {...register("password")}
                  className="pr-10 h-11 sm:h-10 text-base sm:text-sm font-mono bg-card"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPasswordInput((prev) => !prev)}
                  aria-label={
                    showPasswordInput
                      ? "Hide passphrase input"
                      : "Show passphrase input"
                  }
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                >
                  {showPasswordInput ? (
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </Button>
              </div>
              {errors.password && (
                <p role="alert" className="text-[11px] text-danger">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="vault-wifi-notes"
                className="text-xs font-medium text-foreground block"
              >
                Notes (Optional)
              </label>
              <Input
                id="vault-wifi-notes"
                autoComplete="off"
                placeholder="e.g. Building B router VLAN 20"
                {...register("notes")}
                className="h-11 sm:h-10 text-base sm:text-sm bg-card"
              />
              {errors.notes && (
                <p role="alert" className="text-[11px] text-danger">
                  {errors.notes.message}
                </p>
              )}
            </div>
          </fieldset>

          <div className="flex items-center gap-2">
            {editingNetwork && (
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="h-11 sm:h-10 touch:min-h-[44px] text-xs px-4"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={isSaving}
              className="flex-1 h-11 sm:h-10 touch:min-h-[44px] text-xs"
            >
              {isSaving ? (
                <>
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                  <span>
                    {editingNetwork
                      ? "Updating Profile..."
                      : "Encrypting & Saving..."}
                  </span>
                </>
              ) : (
                <span>
                  {editingNetwork
                    ? "Save Profile Changes"
                    : "Add Profile to Vault"}
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
